<template>
    <Toast />
    <div>
        <div class="p-d-flex p-jc-between">
            <p class="profile-title">Profile</p>
            <div v-if="show">
                <Button label="Cancel" class="p-button-outlined button" @click="cancel" />
                <Button label="Save" class="button" @click="savePartnerProfile" />
                <FloatingSaveCancel @save="savePartnerProfile" @cancel="cancel" />
            </div>
        </div>

        <Accordion :activeIndex="0">
            <AccordionTab header="Basic information - Company profile information">
                <div class="p-grid p-fluid">
                    <div class="p-col-12 p-md-6">
                        <p class="field-title">Company name</p>
                        <InputText :disabled="((isChannel || isAdmin) && !hasAgreement)" type="text" v-model="v$.Company_name.$model"
                            @input="inputChange()" placeholder="Enter company name" @keypress="isLetter($event)" />
                        <p v-for="error of v$.Company_name.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please enter the value' }}
                        </p>
                    </div>

                    <div class="p-col-12 p-md-6">
                        <p class="field-title">
                            Company logo
                            <Tooltip
                                description="Recommended size 250*100px. Upload an image well suited for white/light backgrounds." />
                        </p>
                        <div v-if="logoUrl" class="logo-container">
                            <img :src="logoUrl" alt="Channel Logo" class="channel-logo" />
                            <i v-if="isPartner || ((isChannel || isAdmin) && hasAgreement)" class="pi pi-times cross" @click="triggerDeleteFile = true"></i>
                        </div>

                        <div :style="{ visibility: logoUrl ? 'hidden' : 'visible' }">
                            <FileUpload :disabled="((isChannel || isAdmin) && !hasAgreement)" @saved="onSaved" :isImage="isImage"
                                :isAttachment="isAttachment" :id="partnerId" :fieldId="companyLogoName"
                                :fieldName="company_logo" :logoUrl="logoUrl" from="partnerProfile"
                                v-model:triggerDeleteFile="triggerDeleteFile" />
                        </div>
                    </div>

                    <div class="p-col-12 p-md-6" v-if="isAdmin && platformName === 'ArenaCX'">
                        <p class="field-title">ArenaCX Quickbooks ID</p>
                        <InputText :disabled="((isChannel || isAdmin) && !hasAgreement)" v-model="v$.Quickbooks_vendor_ID.$model" @input="inputChange()"
                            placeholder="Enter ArenaCX Quickbooks ID" />
                        <p v-for="error of v$.Quickbooks_vendor_ID.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please enter numeric value of maximum length 15 characters' }}
                        </p>
                    </div>

                    <div class="p-col-12 p-md-6">
                        <p class="field-title">Website</p>
                        <InputText :disabled="(isChannel || isAdmin) && !hasAgreement" type="text" v-model="v$.Website.$model" @input="inputChange()"
                            placeholder="Enter Website" />
                        <p v-for="error of v$.Website.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please enter a valid URL' }}
                        </p>
                    </div>
            
                    <div class="p-col-12 p-md-6">
                        <p class="field-title">Year your organization was founded.
                        </p>
                        <InputText :disabled="((isChannel || isAdmin) && !hasAgreement)" v-model="v$.business_countries.$model" @input="inputChange()"
                            placeholder="Enter the year (1900-2200)" />
                        <p v-for="error of v$.business_countries.$errors" :key="error.$uid" class="error-msg">
                            {{ error.$validator === 'required' ? 'Please enter the year' : 
                               error.$validator === 'numeric' ? 'Please enter numeric value' :
                               error.$validator === 'yearRange' ? 'Year must be between 1900 and 2200' :
                               'Please enter a valid year' }}
                        </p>
                    </div>
                    <div class="p-col-12 p-md-6" v-if="isAdmin">
                        <p class="field-title">Please select directory access for this partner</p>
                        <TreeSelect :disabled="((isChannel || isAdmin) && !hasAgreement)" v-model="directory_access" :options="treeDirectoryOptions" selectionMode="checkbox"
                            placeholder="Select Directory Access" @change="handleTreeSelectChange" />
                        <p v-for="error of v$.directory_access.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please select directory access' }}
                        </p>
                    </div>


                    
                <!-- Vendor Rank Fields - Visible and Editable only by Site Admins -->
                    <div class="p-col-12 p-md-6" v-if="isAdmin">
                        <p class="field-title">Vendor Rank</p>
                        <InputText 
                            :disabled="origin !== platform"
                            v-model="v$.Vendor_Rank.$model"
                            @input="inputChange()"
                            type="number"
                            placeholder="Enter vendor rank"
                        />
                        <p v-for="error of v$.Vendor_Rank.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please enter a valid positive integer' }}
                        </p>
                    </div>

                    <div class="p-col-12 p-md-6" v-if="isAdmin">
                        <p class="field-title">Vendor Rank Boost</p>
                        <InputText 
                            :disabled="origin !== platform"
                            v-model="v$.Vendor_Rank_Boost.$model"
                            @input="inputChange()"
                            type="number"
                            placeholder="Enter vendor rank boost"
                        />
                        <p v-for="error of v$.Vendor_Rank_Boost.$errors" :key="error.$uid" class="error-msg">
                            {{ 'Please enter a valid positive integer' }}
                        </p>
                    </div>


                </div>


                <ProfileAddress :isChannel="isChannel" :saveInd="saveInd" :locationInfoValues="locationInfoValues" :isPartner="isPartner" :hasAgreement="hasAgreement"
                    @submit="locationInfoValidation" />
                <ProfileDistributors :saveInd="saveInd" :isChannel="isChannel" :disableEditForDifferentOrigin="origin !== platform"
                    :distributorValues="distributorValues" @submit="onDistributorChange" :channels="channels" :partnerOwner="partnerOwner"/>

                <!-- Single Checkbox for ArenaCX Accreditation -->
                <div v-if="isAdmin" class="p-col-12 p-md-6">
                    <div class="field-checkbox ml-1rem mb-1rem">
                        <label for="isAccredited" class="ml-1rem checkbox-label">Accredited by ArenaCX</label>
                        <Checkbox v-model="isAccredited" :binary="true" class="ml-1rem" inputId="isAccredited"
                            @change="isAccreditedChange" :disabled="origin !== platform" />
                    </div>
                </div>


                <div class="p-grid p-fluid" v-if="isAdmin">
                    <div class="p-col-12 p-md-12 p-px-5">
                        <div class="field-title">Channels and Channel Links</div>

                        <!-- Table structure for channels and contract templates -->
                        <table class="channels-table">
                            <thead>
                                <tr>
                                    <th>Enable</th>
                                    <th>Channel Name</th>
                                    <!-- <th>Contract Template</th>  -->
                                    <th>ArenaCX-branded Channel Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="channel in channels" :key="channel.subnetwork_id">
                                    <!-- Enable Switch Column -->
                                    <td class="switch-column">
                                        <InputSwitch v-model="channel.enabled" @change="toggleChannel(channel)" :disabled="origin !== platform" />
                                    </td>

                                    <!-- Channel Name Column -->
                                    <td class="channel-name-column">
                                        {{ channel.name }}
                                    </td>

                                    <!-- Contract Template Column -->
                                    <!-- <td class="template-column">
                                        <select v-model="channel.selectedTemplate" disabled class="template-dropdown"
                                            @change="changeHandler">
                                            <option :disabled="!channel.isContractTemplate" value="self">
                                                {{
                                                    channel.isContractTemplate
                                                        ? `${channel?.name} [Template Available]`
                                                        : `${channel?.name} [Template Unavailable]`
                                                }}
                                            </option>
                                            <option value="default">Default [ArenaCX]</option>
                                        </select>
                                    </td>  -->

                                    <td class="channel-link-column">
                                        <a :href="`https://platform.arenacx.com/?ch=${channel.subnetwork_code}`"
                                            target="_blank">
                                            {{ `https://platform.arenacx.com/?ch=${channel.subnetwork_code}` }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </div>
</template>

<script>
import UserManagementService from '../../service/Usermanagement';
import { required, email, maxLength, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import ProfileService from '../../service/ProfileSservice';
import PartnerDetailsService from '../../service/PartnerDetailsService';
import { onMounted } from '@vue/runtime-core';
import { getAccount, getAccountId, getIsChannel, getIsPartner } from '../../service/common';
import CurrentUserService from '../../service/CurrentUserService';
import Tooltip from '../Tooltip.vue';
import ProfileAddress from './ProfileAddress.vue';
import ProfileDistributors from '../ProfileDistributors.vue';
import CountryService from '../../service/CountryService';
import FileUpload from '../common/FileUpload.vue';
import FloatingSaveCancel from '../common/FloatingSaveCancel.vue';
import { directoryTree } from '../../service/common/directoryCode';
import AccountMangementService from '../../service/AccountManagementService';
import AccountService from '../../service/AccountService';
import AccreditionService from '../../service/AccreditionService';
import AnswerBankService from '../../service/AnswerBankService';
import PartnerRankingService from '../../service/PartnerRankingService';
import { TreeSelect } from 'primevue/treeselect';
import { Chip } from 'primevue/chip';
import EventBus from '../../event-bus';




const customDomainCheck = (value) => {
    if (value) {
        //#31wdfdn -- decided this was always going to be a problem, so always return true.
        //            every regex variation has flaws
        return true;

        // PREVIOUS VERSION: didn't like any path, or ".outsourcing" vs ".com"
        // const regex = new RegExp(/(?:(?:[a-zA-Z0-9])(?:[-.][a-zA-Z0-9])?)+(?:\.[a-z]{2,6})+$/);
        // return regex.test(value);
    }
    return true;
};

const positiveInt = (value) => {
    if (value && (value.length > 15 || value < 0 || !/^[1-9]\d*$/.test(value))) return false;
    else return true;
};

export default {
    components: {
        ProfileAddress,
        Tooltip,
        FileUpload,
        FloatingSaveCancel,
        TreeSelect,
        ProfileDistributors,
    },
    data() {
        return {
            Company_name: null,
            Quickbooks_vendor_ID: null,
            Website: '',
            Primary_contact_person: '',
            Primary_contact_title: '',
            Primary_contact_email: '',
            escalation_contact_name: '',
            escalation_contact_title: '',
            escalation_contact_email: '',
            escalation_contact_phone: '',
            business_countries: null,
            directory_access: [],
            Phone_number: null,
            companyLogoName: '',
            profileForm: {},
            responseData: [],
            show: false,
            isAdmin: false,
            isImage: true,
            isAttachment: false,
            partnerId: null,
            locationInfoValues: null,
            saveInd: false,
            countries: null,
            logoUrl: null,
            mappedCountry: [],
            fileName: null,
            deleteFile: false,
            treeDirectoryOptions: [],
            channels: [],
            partnerChannels: [],
            isChannel: false,
            accountData: null,
            isAccredited: false,
            triggerDeleteFile: false,
            distributorValues: [],
            partnerOwner: null,
            platform: process.env.VUE_APP_PLATFORM_ID,
            defaultSubnetworkId: process.env.VUE_APP_DEFAULT_SUBNETWORK_ID,
            origin: null,
            currentUserService: null,
            isPartner: false,
            hasAgreement: false,
            USER_ROLE_IDS: {
                MEMBER: '40b61568-4b0c-11ec-a04e-c1630e4615a6',
                CHANNEL: '140e718e-c16d-4f1d-ad62-15216aeda4a8',
                PARTNER: '40c16ee0-4b0c-11ec-a04e-c1630e4615a6',
                CONTRIBUTOR: '4ec156ea-f65d-4657-a31c-132a010d7141'
            },
            platformName: process.env.VUE_APP_PLATFORM_NAME,
            envType: process.env.VUE_APP_ENV_TYPE,
            Vendor_Rank: null,
            Vendor_Rank_Boost: null
        };
    },

    setup() {
        return { v$: useVuelidate() };
    },
    validations() {
        return {
            Company_name: !this.isChannel ? { required, $autoDirty: true } : {},
            Quickbooks_vendor_ID: (this.isAdmin && this.platformName === 'ArenaCX') ? { positiveInt } : {},
            Website: { customDomainCheck },
            Primary_contact_person: { $autoDirty: true },
            Primary_contact_title: { $autoDirty: true },
            Primary_contact_email: { email, $autoDirty: true },
            escalation_contact_name: { $autoDirty: true },
            escalation_contact_title: { $autoDirty: true },
            escalation_contact_email: { email, $autoDirty: true },
            escalation_contact_phone: { maxLength: maxLength(25), minLength: minLength(10), $autoDirty: true },
            business_countries: { 
                $autoDirty: true, 
                positiveInt,
                yearRange: (value) => {
                    if (!value) return true; // Let required validation handle empty values
                    const year = parseInt(value);
                    return !isNaN(year) && year >= 1900 && year <= 2200;
                }
            },
            directory_access: this.isAdmin ? { $autoDirty: true, required } : {},
            Phone_number: { maxLength: maxLength(15), minLength: minLength(10) },
            Vendor_Rank: this.isAdmin ? { positiveInt, $autoDirty: true } : {},
            Vendor_Rank_Boost: this.isAdmin ? { positiveInt, $autoDirty: true } : {},
        };
    },
    async created() {
        this.profileService = new ProfileService();
        this.countryService = new CountryService();
        this.accountMangementService = new AccountMangementService();
        this.accountService = new AccountService();
        this.accreditionService = new AccreditionService();
        this.currentUserService = new CurrentUserService();
        this.partnerDetailsService = new PartnerDetailsService()
        this.userManagementService = new UserManagementService();
        this.partnerRankingService = new PartnerRankingService();
        this.isChannel = getIsChannel();
        this.isPartner = getIsPartner();
        this.isAdmin = getAccount();

        // Initialize data in sequence
        await this.initializeDirectoryOptions(); // Wait for tree options to load first
        this.getProfile(); // Then get profile data

        this.countryService.getCountries().then((countries) => {
            this.countries = countries;
        });
        this.accountMangementService.getChannels().then((res) => {
            this.channels = res.data.data;
        });
        await this.accountService.getAccountData(getAccountId()).then((res) => {
            this.accountData = res.data;
        });
        this.isAccredited = this.accountData?.accredition_details?.length > 0 ? true : false;
        this.partnerOwner = this.accountData?.owner_subnetwork;
    },
    watch: {
        $route() {
            this.partnerId = getAccountId();
        },
        partnerId() {
            this.profileService = new ProfileService();
            this.getProfile();
            this.isAdmin = getAccount();
        },
    },
    methods: {
        onSaved(file) {
            this.logoUrl = file;
        },

        // Helper method to check if distributor contacts are editable by current user
        isDistributorEditable(distributor) {
            // Contacts are editable if:
            // 1. User is the owner of the distributor (distributor.isOwner === true)
            // 2. User is a partner (isPartner === true)
            // 3. User is admin and platform is ArenaCX (isAdmin && platformName === 'ArenaCX')
            const isEditable = distributor.isOwner || this.isPartner || (this.isAdmin && this.platformName === 'ArenaCX');
            
            console.log(`[isDistributorEditable] Checking editability for distributor ${this.getChannelName(distributor.subnetwork_id)}:`, {
                isOwner: distributor.isOwner,
                isPartner: this.isPartner,
                isAdmin: this.isAdmin,
                platformName: this.platformName,
                isEditable: isEditable
            });
            
            return isEditable;
        },

        // Helper method to check if email should be allowed based on environment
        shouldCreateUser(email) {
            console.log(`[shouldCreateUser] Checking email: ${email}, Environment: ${this.envType}`);
            
            if (!email) {
                console.warn(`[shouldCreateUser] Email is empty or null`);
                return false;
            }
            
            // If environment is PROD, allow all emails
            if (this.envType === 'PROD') {
                console.log(`[shouldCreateUser] PROD environment - allowing all emails`);
                return true;
            }

            // For non-PROD environments, only allow specific domains
            const allowedDomains = [
                'techjays.com',
                'ajito.io',
                'arenacx.com',
                'torchbearer.llc',
                'tbconsulting.llc',
                'gmail.com'
            ];

            // Extract domain from email
            const emailParts = email.toLowerCase().split('@');
            if (emailParts.length !== 2) {
                console.warn(`[shouldCreateUser] Invalid email format: ${email}`);
                return false;
            }
            
            const emailDomain = emailParts[1];
            const isAllowed = allowedDomains.includes(emailDomain);
            
            console.log(`[shouldCreateUser] Email domain: ${emailDomain}, Allowed: ${isAllowed}, Allowed domains: ${allowedDomains.join(', ')}`);
            
            return isAllowed;
        },

        isAccreditedChange() {
            const payload = {
                accountId: getAccountId(),
                accredition: this.isAccredited ? ['f4cd7264-3f00-463b-b2e9-d9e69c3ec193'] : [],
            };
            this.accreditionService.updateAccredition(payload).then((res) => {
                if (res.status === 200) {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Accreditation updated successfully!',
                        detail: '',
                        life: 3000,
                    });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Something went wrong!', detail: '', life: 3000 });
                }
            });
        },

        toggleChannel(ch) {
            if (!ch.enabled) {
                ch.selectedTemplate = 'default';
            }
            this.show = true;
        },
        changeHandler() {
            this.show = true;
        },

        savePartnerProfile() {
            this.v$.$touch();

            // Set saveInd to true to trigger child component validation
            this.saveInd = true;

            // Use nextTick to ensure child components have time to validate
            this.$nextTick(() => {
                if (this.v$.$error === false) {
                    // Additional validation for distributor contacts
                    if (this.distributorValues && this.distributorValues.length > 0) {
                        let hasValidationError = false;
                        
                        for (const distributor of this.distributorValues) {
                            if (distributor.contacts && distributor.contacts.length > 0) {
                                const hasPrimaryContact = distributor.contacts.some(contact => contact.isPrimary);
                                if (!hasPrimaryContact) {
                                    // Find channel name for better error message
                                    const channel = this.channels.find(c => c.subnetwork_id === distributor.subnetwork_id);
                                    const channelName = channel ? channel.name : 'Unknown';
                                    
                                    this.$toast.add({
                                        severity: 'error',
                                        summary: 'Validation Error',
                                        detail: `At least one primary contact is required for ${channelName}`,
                                        life: 5000,
                                    });
                                    hasValidationError = true;
                                    break;
                                }
                            }
                        }
                        
                        if (hasValidationError) {
                            this.saveInd = false;
                            return;
                        }
                    }
                    
                    this.submitProfile();
                } else {
                    this.v$.$errors.forEach((d) => {
                        if (d.$property === 'Company_name') {
                            this.$toast.add({
                                severity: 'error',
                                summary: 'Error while creating user',
                                detail: 'Please update mandatory field',
                                life: 3000,
                            });
                        } else {
                            this.$toast.add({
                                severity: 'error',
                                summary: 'Error while creating user',
                                detail: 'Please enter a valid input',
                                life: 3000,
                            });
                        }
                    });
                }

                // Reset saveInd after validation
                this.saveInd = false;
            });
        },
        locationInfoValidation(silentErrors, locationInfoValuesData) {
            this.locationInfoValues = locationInfoValuesData;
            this.inputChange();
        },

        onDistributorChange(silentErrors, distributorValuesData) {
            this.distributorValues = distributorValuesData;
            
            // If there are validation errors, don't allow saving
            if (silentErrors) {
                this.show = false;
                return;
            }
            
            this.inputChange();
        },
        stringCheck(value) {
            if (value !== null && value !== undefined) {
                return value.toString().trim();
            }
        },
        inputChange() {
            this.show = true;
        },
        isLetter(e) {
            let char = String.fromCharCode(e.keyCode);
            if (!/[^a-zA-Z0-9 ,.]/.test(char)) return true;
            else e.preventDefault();
        },
        isNumber(e) {
            let char = String.fromCharCode(e.keyCode);
            if (!/[^0-9+]/.test(char)) return true;
            else e.preventDefault();
        },
        phoneKeyUp() {
            if (this.escalation_contact_phone !== '' || this.Phone_number !== '') {
                this.show = true;
            }
        },
        async submitProfile() {
            this.show = false;
            let payload = {};
            var filteredData = this.responseData.filter((element) => {
                if (element.field_name !== 'company_logo') {
                    return element;
                }
            });
            filteredData.forEach((element) => {
                if (element.field_name == 'name') {
                    element.json_value = this.stringCheck(this.Company_name);
                    this.Company_name = this.stringCheck(this.Company_name);
                }
                if (element.field_name == 'website') {
                    element.json_value = this.Website;
                }
                if (element.field_name == 'quickbooks_vendor_id' && this.isAdmin) {
                    element.json_value = this.Quickbooks_vendor_ID;
                }
                if (element.field_name == 'vendor_rank' && this.isAdmin) {
                    element.json_value = this.Vendor_Rank;
                }
                if (element.field_name == 'vendor_rank_boost' && this.isAdmin) {
                    element.json_value = this.Vendor_Rank_Boost;
                }

                if (element.field_name == 'escalation_phone_number') {
                    element.json_value = this.escalation_contact_phone;
                }
                if (element.field_name == 'escalation_contact_name') {
                    element.json_value = this.stringCheck(this.escalation_contact_name);
                    this.escalation_contact_name = this.stringCheck(this.escalation_contact_name);
                }
                if (element.field_name == 'escalation_contact_email') {
                    element.json_value = this.escalation_contact_email;
                }
                if (element.field_name == 'escalation_contact_title') {
                    element.json_value = this.stringCheck(this.escalation_contact_title);
                    this.escalation_contact_title = this.stringCheck(this.escalation_contact_title);
                }
                if (element.field_name == 'locations') {
                    this.locationInfoValues?.forEach((ele) => delete ele.countryName);
                    element.json_value = this.locationInfoValues;
                }
                if (element.field_name == 'business_years') {
                    element.json_value = parseInt(this.business_countries);
                }
                if (element.field_name == 'directory_access' && this.isAdmin) {
                    console.log("Test", this.directory_access);
                    element.json_value = Object.keys(this.directory_access)
                        .filter(key => {
                            const node = this.directory_access[key];
                            return node && (node.checked || node.partialChecked);
                        })
                        .map(key => {
                            // Find the corresponding node in treeDirectoryOptions to get its data value
                            const findNode = (nodes) => {
                                for (const node of nodes || []) {
                                    if (node.key === key) {
                                        return node.data;
                                    }
                                    if (node.children) {
                                        const found = findNode(node.children);
                                        if (found) return found;
                                    }
                                }
                                return null;
                            };
                            return findNode(this.treeDirectoryOptions);
                        })
                        .filter(data => data !== null && typeof data === 'number'); // Only keep numeric values
                }
                if (element.field_name == 'distributors') {
                    // Clean distributor data before sending to backend
                    const cleanedDistributors = this.distributorValues.map(distributor => {
                        const cleanedDistributor = { ...distributor };
                        // Remove editing state properties that shouldn't be sent to backend
                        delete cleanedDistributor.isEditingCommissionRate;
                        delete cleanedDistributor.isEditingNotes;
                        delete cleanedDistributor.commission_rate_numeric;
                        return cleanedDistributor;
                    });
                    element.json_value = cleanedDistributors;
                }
            });

            filteredData.forEach((ele) => {
                payload[ele.id] = ele.json_value === undefined ? null : ele.json_value;
            });
            payload.channels = this.channels
                .filter((e) => e.enabled)
                .map((channel) => {
                    return {
                        subnetwork_id: channel.subnetwork_id,
                        contract_subnetwork:
                            channel.selectedTemplate === 'default'
                                ? 'f4cd7264-3f00-463b-b2e9-d9e69c3ec193'
                                : channel.subnetwork_id,
                    };
                });
            console.log("payload >>", payload);
            console.log('========== PROFILE UPDATE INITIATED ==========');
            console.log('Distributor Values Count:', this.distributorValues ? this.distributorValues.length : 0);
            
            this.profileService.updateProfile(payload)
                .then(async (res) => {
                    console.log('Profile Update Response Status:', res?.status);
                    console.log('Profile Update Response:', res);
                    
                    if (res && res.status === 200) {
                        console.log('✅ Profile update successful (200), proceeding with user creation...');
                        // Upsert vendor ranking after successful profile update
                        try {
                            await this.partnerRankingService.upsertVendorRanking({
                                subnetwork_id: this.defaultSubnetworkId,
                                vendor_id: getAccountId(),
                                rank: Number(this.Vendor_Rank) || 0,
                                rank_boost: Number(this.Vendor_Rank_Boost) || 0,
                            });
                        } catch (e) {
                            console.error('Error upserting vendor ranking:', e);
                            this.$toast.add({ severity: 'warn', summary: 'Ranking not saved', detail: 'Profile saved but ranking update failed.', life: 4000 });
                        }

                        // Iterate through each distributor and create users for their contacts
                        console.log('========== USER CREATION PROCESS STARTED ==========');
                        console.log('Profile Update Status: SUCCESS (200)');
                        console.log('Partner ID:', this.partnerId);
                        console.log('Environment Type:', this.envType);
                        console.log('Total Distributors:', this.distributorValues ? this.distributorValues.length : 0);
                        console.log('Distributor Values:', JSON.stringify(this.distributorValues, null, 2));
                        
                        if (!this.distributorValues || this.distributorValues.length === 0) {
                            console.warn('⚠️ USER CREATION SKIPPED: No distributors found in distributorValues');
                            console.log('distributorValues is:', this.distributorValues);
                        } else {
                            for (const distributor of this.distributorValues) {
                                const channelName = this.getChannelName(distributor.subnetwork_id);
                                console.log(`\n--- Processing Distributor: ${channelName} (ID: ${distributor.subnetwork_id}) ---`);
                                console.log('Distributor Data:', JSON.stringify(distributor, null, 2));
                                
                                // Check if distributor contacts are editable by current user
                                const isEditable = this.isDistributorEditable(distributor);
                                if (!isEditable) {
                                    console.warn(`⚠️ SKIPPED ${channelName}: Distributor contacts are not editable by current user - skipping user creation`);
                                    console.log(`  Reason: User cannot edit contacts for this distributor (isOwner: ${distributor.isOwner}, isPartner: ${this.isPartner}, isAdmin: ${this.isAdmin}, platform: ${this.platformName})`);
                                    continue;
                                }
                                
                                console.log(`✓ Distributor ${channelName} is editable - proceeding with user creation`);
                                
                                if (!distributor.contacts) {
                                    console.warn(`⚠️ SKIPPED ${channelName}: distributor.contacts is ${distributor.contacts}`);
                                    continue;
                                }
                                
                                if (distributor.contacts.length === 0) {
                                    console.warn(`⚠️ SKIPPED ${channelName}: No contacts found (contacts array is empty)`);
                                    continue;
                                }
                                
                                console.log(`✓ Found ${distributor.contacts.length} contact(s) for ${channelName}`);
                                
                                // Find primary contact for this distributor
                                const primaryContact = distributor.contacts.find(contact => contact.isPrimary);
                                
                                if (!primaryContact) {
                                    console.warn(`⚠️ SKIPPED Primary Contact for ${channelName}: No primary contact found`);
                                    console.log('Available contacts:', distributor.contacts.map(c => ({ name: c.name, email: c.email, isPrimary: c.isPrimary })));
                                } else {
                                    console.log(`✓ Found Primary Contact: ${primaryContact.name} (${primaryContact.email})`);
                                    
                                    if (!primaryContact.email) {
                                        console.error(`❌ SKIPPED Primary Contact for ${channelName}: Email is missing or empty`);
                                    } else {
                                        const shouldCreate = this.shouldCreateUser(primaryContact.email);
                                        console.log(`Email domain check for ${primaryContact.email}: ${shouldCreate ? 'ALLOWED' : 'BLOCKED'}`);
                                        
                                        if (shouldCreate) {
                                            try {
                                                // Create user for Primary Contact (PARTNER role)
                                                const userCreationPayload = {
                                                    email: primaryContact.email,
                                                    account_id: this.partnerId,
                                                    user_role_id: this.USER_ROLE_IDS.PARTNER,
                                                    last_name: primaryContact.name.split(' ').slice(1).join(' ') || '',
                                                    first_name: primaryContact.name.split(' ')[0] || '',
                                                };
                                                console.log(`🚀 ATTEMPTING User Creation for Primary Contact in ${channelName}:`, userCreationPayload);
                                                
                                                const userCreationResponse = await this.userManagementService.addNewUser(userCreationPayload);
                                                
                                                if (userCreationResponse && userCreationResponse.status === 200) {
                                                    console.log(`✅ SUCCESS: Primary Contact user created for ${primaryContact.email} in ${channelName}`);
                                                    console.log('Response:', userCreationResponse.data);
                                                } else {
                                                    console.error(`❌ FAILED: Primary Contact user creation returned non-200 status for ${primaryContact.email} in ${channelName}`);
                                                    console.error('Response:', userCreationResponse);
                                                }
                                            } catch (error) {
                                                console.error(`❌ ERROR: Failed to create user for Primary Contact ${primaryContact.email} in ${channelName}:`, error);
                                                console.error('Error details:', {
                                                    message: error.message,
                                                    response: error.response?.data,
                                                    status: error.response?.status
                                                });
                                            }
                                        } else {
                                            console.log(`⏭️ SKIPPED: Primary Contact ${primaryContact.email} in ${channelName} - email domain not allowed in ${this.envType} environment`);
                                        }
                                    }
                                }

                                // Create users for all RFP Contributors (excluding Primary Contact)
                                const rfpContributors = distributor.contacts.filter(contact => 
                                    contact.isRFPContributor && !contact.isPrimary
                                );
                                
                                console.log(`Found ${rfpContributors.length} RFP Contributor(s) (excluding primary) for ${channelName}`);
                                
                                if (rfpContributors.length === 0) {
                                    console.log(`ℹ️ No RFP Contributors to process for ${channelName}`);
                                } else {
                                    for (const rfpContributor of rfpContributors) {
                                        console.log(`\n  Processing RFP Contributor: ${rfpContributor.name} (${rfpContributor.email})`);
                                        
                                        if (!rfpContributor.email) {
                                            console.error(`  ❌ SKIPPED RFP Contributor ${rfpContributor.name}: Email is missing or empty`);
                                            continue;
                                        }
                                        
                                        const shouldCreate = this.shouldCreateUser(rfpContributor.email);
                                        console.log(`  Email domain check for ${rfpContributor.email}: ${shouldCreate ? 'ALLOWED' : 'BLOCKED'}`);
                                        
                                        if (shouldCreate) {
                                            try {
                                                const rfpUserPayload = {
                                                    email: rfpContributor.email,
                                                    account_id: this.partnerId,
                                                    user_role_id: this.USER_ROLE_IDS.CONTRIBUTOR, // RFP Contributor role
                                                    last_name: rfpContributor.name.split(' ').slice(1).join(' ') || '',
                                                    first_name: rfpContributor.name.split(' ')[0] || '',
                                                };
                                                console.log(`  🚀 ATTEMPTING User Creation for RFP Contributor in ${channelName}:`, rfpUserPayload);
                                                
                                                const rfpUserResponse = await this.userManagementService.addNewUser(rfpUserPayload);
                                                
                                                if (rfpUserResponse && rfpUserResponse.status === 200) {
                                                    console.log(`  ✅ SUCCESS: RFP Contributor user created for ${rfpContributor.email} in ${channelName}`);
                                                    console.log('  Response:', rfpUserResponse.data);
                                                } else {
                                                    console.error(`  ❌ FAILED: RFP Contributor user creation returned non-200 status for ${rfpContributor.email} in ${channelName}`);
                                                    console.error('  Response:', rfpUserResponse);
                                                }
                                            } catch (error) {
                                                console.error(`  ❌ ERROR: Failed to create user for RFP Contributor ${rfpContributor.email} in ${channelName}:`, error);
                                                console.error('  Error details:', {
                                                    message: error.message,
                                                    response: error.response?.data,
                                                    status: error.response?.status
                                                });
                                            }
                                        } else {
                                            console.log(`  ⏭️ SKIPPED: RFP Contributor ${rfpContributor.email} in ${channelName} - email domain not allowed in ${this.envType} environment`);
                                        }
                                    }
                                }
                                
                                console.log(`--- Finished Processing Distributor: ${channelName} ---\n`);
                            }
                        }
                        
                        console.log('========== USER CREATION PROCESS COMPLETED ==========');
                        // Menu refresh not needed - profile updates don't affect menu structure
                        this.getProfile();
                        this.show = false;
                        this.$toast.add({ severity: 'success', summary: res.data.data, detail: '', life: 3000 });
                    } else {
                        // Handle non-200 status responses
                        console.error('❌ PROFILE UPDATE FAILED: Non-200 status code');
                        console.error('Response status:', res?.status);
                        console.error('Response data:', res?.data);
                        console.warn('⚠️ USER CREATION SKIPPED: Profile update did not return 200 status');
                        
                        let errorMsg = 'Error while saving profile';
                        if (res && res.data) {
                            errorMsg = res.data?.data || res.data?.message || res.data || errorMsg;
                        }
                        this.$toast.add({ 
                            severity: 'error', 
                            summary: 'Error', 
                            detail: errorMsg, 
                            life: 5000 
                        });
                    }
                })
                .catch((error) => {
                    // Handle error response - axios throws errors for 4xx and 5xx status codes
                    console.error('========== PROFILE UPDATE ERROR ==========');
                    console.error('❌ ERROR in updateProfile:', error);
                    console.error('Error response:', error.response);
                    console.error('Error response data:', error.response?.data);
                    console.error('Error message:', error.message);
                    console.warn('⚠️ USER CREATION SKIPPED: Profile update threw an error');
                    console.error('==========================================');
                    
                    let errorMsg = 'An error occurred while saving the profile.';
                    
                    if (error.response && error.response.data) {
                        const responseData = error.response.data;
                        console.log('Response data type:', typeof responseData);
                        console.log('Response data:', responseData);
                        
                        // Error structure: { statusCode: 400, message: "Bad Request", data: "error message" }
                        // Try to get the actual error message from the data field
                        if (responseData && typeof responseData === 'object' && responseData.data) {
                            // If responseData is an object with a 'data' property containing the message
                            errorMsg = responseData.data;
                        } else if (responseData && typeof responseData === 'object' && responseData.message) {
                            errorMsg = responseData.message;
                        } else if (typeof responseData === 'string') {
                            // If responseData is directly a string
                            errorMsg = responseData;
                        } else if (responseData) {
                            // Fallback: stringify the response
                            errorMsg = JSON.stringify(responseData);
                        }
                    } else if (error.message) {
                        errorMsg = error.message;
                    }
                    
                    console.log('Showing error toast with message:', errorMsg);
                    this.$toast.add({ 
                        severity: 'error', 
                        summary: 'Error', 
                        detail: errorMsg, 
                        life: 5000 
                    });
                });
        },

        cancel() {
            this.getProfile();
            this.show = false;
        },
        async getProfile() {
            try {
                const data = await this.profileService.getProfileById();
                if (data.status === 200) {
                    var resData = data.data.data.data[0].data;
                    this.partnerChannels = data.data.data.channels;
                    const defaultSubnetworkId = 'f4cd7264-3f00-463b-b2e9-d9e69c3ec193';
                    //promise. all to fetch the if the contract template exists in partnerchannels

                    console.log({ 'Channels ::': this.channels });

                    this.channels = this.channels
                        .map((channel) => {
                            const partnerCh = this.partnerChannels.find(
                                (partnerChannel) => partnerChannel.subnetwork_id === channel.subnetwork_id
                            );

                            // Default values for channel properties
                            const updatedChannel = {
                                ...channel,
                                isContractTemplate: false, // Initial default value
                                enabled: partnerCh?.subnetwork_id === channel?.subnetwork_id || false,
                                selectedTemplate:
                                    partnerCh?.contract_subnetwork === defaultSubnetworkId || !partnerCh?.contract_subnetwork
                                        ? 'default'
                                        : 'self',
                            };

                            // Asynchronously update isContractTemplate without waiting
                            // this.accountMangementService
                            //     .getContractTemplate(channel.subnetwork_id)
                            //     .then((resp) => {
                            //         const templateExists = resp.data.data.templateExists;
                            //         console.log('templateExists:', templateExists);

                            //         // Update the isContractTemplate field
                            //         updatedChannel.isContractTemplate = templateExists;

                            //         // Update this.channels with the modified channel object
                            //         this.channels = this.channels.map((ch) =>
                            //             ch.subnetwork_id === channel.subnetwork_id ? updatedChannel : ch
                            //         );
                            //     })
                            //     .catch((error) => {
                            //         console.error('Error fetching contract template:', error);
                            //     });

                            return updatedChannel;
                        })
                        .sort((a, b) => {
                            if (a.subnetwork_id === defaultSubnetworkId) {
                                return -1;
                            }
                            return 1;
                        });

                    //enabled is true if its present in this.partnerchannels

                    resData.forEach((element) => {
                        // 8685m4xxm -- weird newline characters at end of emails causing validation trouble
                        if (typeof element.json_value === 'string') {
                            element.json_value = element.json_value.trim(); // trim() does everything!
                        }

                        if (element.json_value === 'null') {
                            element.json_value = null;
                        }

                        if (element.field_name == 'name') {
                            this.Company_name = element.json_value;
                        }
                        if (element.field_name == 'website') {
                            this.Website = element.json_value;
                        }
                        if (element.field_name == 'quickbooks_vendor_id' && this.isAdmin) {
                            this.Quickbooks_vendor_ID = element.json_value;
                        }
                        if (element.field_name == 'vendor_rank' && this.isAdmin) {
                            this.Vendor_Rank = element.json_value;
                        }
                        if (element.field_name == 'vendor_rank_boost' && this.isAdmin) {
                            this.Vendor_Rank_Boost = element.json_value;
                        }
                        if (element.field_name == 'phone') {
                            this.Phone_number = element.json_value;
                        }
                        if (element.field_name == 'primary_contact_name') {
                            this.Primary_contact_person = element.json_value;
                        }
                        if (element.field_name == 'primary_contact_title') {
                            this.Primary_contact_title = element.json_value;
                        }
                        if (element.field_name == 'email') {
                            this.Primary_contact_email = element.json_value;
                        }
                        if (element.field_name == 'escalation_phone_number') {
                            this.escalation_contact_phone = element.json_value;
                        }
                        if (element.field_name == 'escalation_contact_name') {
                            this.escalation_contact_name = element.json_value;
                        }
                        if (element.field_name == 'escalation_contact_email') {
                            this.escalation_contact_email = element.json_value;
                        }
                        if (element.field_name == 'escalation_contact_title') {
                            this.escalation_contact_title = element.json_value;
                        }
                        if (element.field_name == 'locations') {
                            this.locationInfoValues = element.json_value;
                        }
                        if (element.field_name == 'business_years') {
                            this.business_countries = element.json_value;
                        }

                        if (element.field_name == 'company_logo') {
                            this.companyLogoName = element.id;
                            this.partnerId = getAccountId();
                            this.logoUrl =
                                element?.other?.attachments && element?.other?.attachments.length > 0
                                    ? element?.other?.attachments[0]
                                    : null;
                            if (this.logoUrl) {
                                var splitLink = this.logoUrl?.split('/');
                                this.fileName = splitLink[splitLink.length - 1];
                            }
                        }
                        if (element.field_name == 'directory_access' && this.isAdmin) {
                            // Make sure treeDirectoryOptions is loaded before processing directory_access
                            if (this.treeDirectoryOptions.length > 0 && element.json_value) {
                                const numericValues = element.json_value.filter(value => typeof value === 'number');
                                this.directory_access = this.findSelectedNodes(this.treeDirectoryOptions, numericValues);
                            }
                        }
                        if (element.field_name == 'distributors') {
                            if(Array.isArray(element.json_value)){
                                // Filter distributors to only show those with matching subnetwork_id in channels
                                const channelSubnetworkIds = this.channels.map(channel => channel.subnetwork_id);
                                let filteredDistributors = element.json_value.filter((distributor) => 
                                    channelSubnetworkIds.includes(distributor.subnetwork_id)
                                );
                                
                                // Sort distributors so that default ones appear first
                                this.distributorValues = filteredDistributors.sort((a, b) => {
                                    // If a is default and b is not, a comes first
                                    if (a.is_default && !b.is_default) {
                                        return -1;
                                    }
                                    // If b is default and a is not, b comes first
                                    if (!a.is_default && b.is_default) {
                                        return 1;
                                    }
                                    // If both have the same default status, maintain original order
                                    return 0;
                                });
                            } else {
                                this.distributorValues = [];
                            }
                        }
                    });
                    //data.data.data.channels contains the channels data name, subnetwork_id.With
                    this.origin = data.data.data.origin || null;
                    this.hasAgreement = await this.partnerDetailsService.checkIfAdminHasAgreementWithVendor(this.isChannel, this.defaultSubnetworkId, this.distributorValues)
                    console.log("Has Agreement::", this.hasAgreement)
                    // Fetch vendor ranking and set defaults if not found
                    try {
                        const rankingRes = await this.partnerRankingService.getVendorRanking(this.defaultSubnetworkId, getAccountId());
                        const ranking = rankingRes?.data || {};
                        this.Vendor_Rank = (ranking.rank ?? 0);
                        this.Vendor_Rank_Boost = (ranking.rank_boost ?? 0);
                    } catch (e) {
                        console.error('Error fetching vendor ranking:', e);
                        this.Vendor_Rank = 0;
                        this.Vendor_Rank_Boost = 0;
                    }
                    return (this.responseData = resData);
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                // Reset values as before
                this.Company_name = '';
                this.Quickbooks_vendor_ID = '';
                this.Website = '';
                this.Primary_contact_name = '';
                this.Phone_number = '';
                this.Vendor_Rank = null;
                this.Vendor_Rank_Boost = null;
            }
        },
        async initializeDirectoryOptions() {
            try {
                const answerBankService = new AnswerBankService();
                const treeData = await answerBankService.getDirectoryTreeData();

                // Transform the tree data to include only selectable leaf nodes
                this.treeDirectoryOptions = this.transformTreeData(treeData);
            } catch (error) {
                console.error('Error initializing directory options:', error);
                this.treeDirectoryOptions = [];
            }
        },

        transformTreeData(tree) {
            const allowedLabels = ["Talent", "Technology", "Transformation"];

            return tree.map((node) => {
                if (!allowedLabels.includes(node.label)) {
                    return null;
                }

                const transformedNode = {
                    key: node.key,
                    label: node.label,
                    selectable: false, // Make the parent node non-selectable
                    data: node.data || node.key, // Ensure data is set for selection
                };

                if (node.label === "Talent") {
                    // Add "BPO" as a subcategory under "Talent"

                    transformedNode.children = [
                        {
                            children: null,
                            key: "Talent-sub",
                            label: "BPO",
                            data: 2,
                            selectable: true
                        },
                        ...(node.children || []).map(child => ({
                            key: child.key,
                            label: child.label,
                            data: child.data,
                            selectable: child.selectable !== false,
                            children: child.children || null
                        })),
                    ];
                } else if (node.children) {
                    transformedNode.children = node.children.map((child) => {
                        const hasSubservices = child.children && child.children.length > 0;
                        return {
                            key: child.key,
                            label: child.label,
                            data: hasSubservices ? null : child.data, // Only set data if it's a leaf node
                            selectable: !hasSubservices,
                            partialSelected: hasSubservices, // Allow partial selection for parent nodes
                            children: hasSubservices ? child.children.map((subChild) => ({
                                key: subChild.key,
                                label: subChild.label,
                                data: subChild.data,
                                selectable: true
                            })) : null,
                        };
                    });
                }
                return transformedNode;
            }).filter(node => node !== null);

        },

        findSelectedNodes(tree, selectedCodes) {
            let selected = {};  // Change back to object since TreeSelect expects an object for checked states

            const traverse = (nodes) => {
                if (!nodes) return;

                nodes.forEach((node) => {
                    // Check if this node's data matches any selected code
                    if (node.data && selectedCodes.includes(node.data)) {
                        selected[node.key] = {
                            checked: true,
                            partialChecked: false
                        };
                    }

                    // If node has children, traverse them
                    if (node.children) {
                        // Check if any children are selected
                        const childrenSelected = node.children.some(child =>
                            child.data && selectedCodes.includes(child.data)
                        );

                        // If any children are selected, mark parent as partially checked
                        if (childrenSelected) {
                            selected[node.key] = {
                                checked: false,
                                partialChecked: true
                            };
                        }

                        traverse(node.children);
                    }
                });
            };

            traverse(tree);
            return selected;
        },

        handleTreeSelectChange(selectedKeys) {
            // Create a copy of the selected keys to modify
            const adjustedSelection = { ...selectedKeys };

            // Traverse the tree to adjust selection
            const adjustSelection = (nodes) => {
                nodes.forEach((node) => {
                    if (node.children) {
                        // Check if all children are selected
                        const allChildrenSelected = node.children.every(child =>
                            adjustedSelection[child.key] && adjustedSelection[child.key].checked
                        );

                        if (allChildrenSelected) {
                            // If all children are selected, uncheck the parent
                            adjustedSelection[node.key] = {
                                checked: false,
                                partialChecked: true
                            };
                        } else {
                            // If not all children are selected, check if any are selected
                            const anyChildSelected = node.children.some(child =>
                                adjustedSelection[child.key] && (adjustedSelection[child.key].checked || adjustedSelection[child.key].partialChecked)
                            );

                            if (anyChildSelected) {
                                adjustedSelection[node.key] = {
                                    checked: false,
                                    partialChecked: true
                                };
                            }
                        }

                        // Recursively adjust children
                        adjustSelection(node.children);
                    }
                });
            };

            adjustSelection(this.treeDirectoryOptions);

            // Update the model with the adjusted selection
            this.directory_access = adjustedSelection;

            // Ensure the "Save" button is shown
            this.show = true;
        },

        getChannelName(subnetwork_id) {
            const channel = this.channels.find(c => c.subnetwork_id === subnetwork_id);
            return channel ? channel.name : 'Unknown';
        },
    },
};
</script>

<style scoped lang="scss">
::v-deep(.p-multiselect) {
    margin-left: 1rem;
    max-width: 30rem;
}

.custom-file-input::-webkit-file-upload-button {
    visibility: hidden;
}

.uploadButton {
    display: none;
}

.uploadLabel {
    border: 1px solid #949ba1;
    display: inline-block;
    padding: 10px;
    cursor: pointer;
    border-radius: 5px;
    background-color: transparent;
    color: rgb(109, 107, 107);
    width: 30rem;
    margin-left: 1rem;
    background-image: url('../../assets/Upload.svg');
    background-position: 95% center;
    background-repeat: no-repeat;
}

::v-deep(.p-accordion-header) {
    font-size: large;
}

::v-deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link),
::v-deep(.p-accordion .p-accordion-tab .p-accordion-header .p-accordion-header-link):hover {
    background-color: rgb(228, 228, 228);
    font-weight: 500;
    margin-top: 1rem;
}

::v-deep(.eden-color) {
    background: #0f5b60;
}

::v-deep(.p-button) {
    background: #00796b !important;
    width: 8rem !important;
    border: 2px solid #00796b !important;
}

::v-deep(.p-button.p-button-outlined) {
    background-color: transparent !important;
    color: #00796b !important;
    border: 1px solid;
    margin-right: 1.25rem;
}

.profile-title {
    font-weight: 700;
    font-family: 'Prompt', sans-serif;
    font-size: 27px;
    margin-left: 1rem;
}

::v-deep(.p-inputtext) {
    margin-left: 1rem;
    max-width: 30rem;
}

.field-title {
    margin-left: 1rem;
    font-size: 1.2rem;
    font-weight: 400;
    max-width: 30rem;
    margin-bottom: 1.5rem;
}

.error-msg {
    margin-left: 1.25rem;
    color: red;
    font-size: 1rem;
    font-weight: 400;
}

.link {
    position: relative;
    left: 1.5rem;
    top: 0.5rem;
    cursor: pointer;
}

.link a {
    color: blue;
}

.attach-file {
    color: #126a70;
    margin-left: 3rem;
}

/* Table styles */
.channels-table {
    width: 100%;
    border-collapse: collapse;
    margin-left: 10px;
    margin-top: 10px;
}

.channels-table th,
.channels-table td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

/* Column styles */
.switch-column {
    width: 10%;
}

.channel-name-column {
    width: 20%;
    font-size: 14px;
}

.template-column {
    width: 20%;
}

.channel-link-column {
    width: 40%;
}

/* Dropdown styles */
/* Ensure the dropdown fills the cell */
.template-dropdown {
    width: 100%;
    padding: 5px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: white;
    box-sizing: border-box;
}

/* Style when dropdown is focused (active state) */
.template-dropdown:focus {
    outline: none;
    border-color: #007bff;
    /* Add blue border on focus */
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    /* Subtle blue glow effect */
    background-color: white;
    /* Keep background white on focus */
    color: black;
    /* Ensure text color is black */
}

/* Change the caret (dropdown arrow) color if needed */
.template-dropdown::-ms-expand {
    color: black;
}

/* Optional - Style hover and active states for better UX */
.template-dropdown:hover {
    border-color: #007bff;
    /* Blue border on hover */
}

.template-dropdown:active {
    border-color: #007bff;
}

.channels-table th {
    font-weight: 400;
}

.template-dropdown option {
    border-radius: 0px;
    /* Remove any border-radius on the expanded options */
    background-color: white;
    color: black;
}

.ml-1rem {
    margin-left: 1rem;
}

.mb-1rem {
    margin-bottom: 2rem;
}

/* Customize the dropdown open list */
.template-dropdown:focus option {
    border-radius: 0px;
    /* Ensure no rounded corners for the dropdown list */
    background-color: white;
    color: black;
}

.logo-container {
    position: relative;
}

.channel-logo {
    margin-bottom: 1rem;
    margin-left: 1rem;
    height: 60px;
    object-fit: cover;
}

.cross {
    cursor: pointer !important;
    margin-left: 0.5rem;
    position: absolute;
}

.directory-select {
    margin-left: 1rem;
    max-width: 30rem;

    ul {
        list-style-type: none;
        padding: 0;

        li {
            margin-bottom: 0.5rem;

            strong {
                display: block;
                margin-bottom: 0.5rem;
            }
        }
    }
}

.directory-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

::v-deep(.p-treeselect) {
    width: 100%;
    margin-left: 1rem;
    max-width: 30rem;
    height: 41px;
}

::v-deep(.p-treeselect-label) {
    padding: 12px;
}
</style>
